import React from 'react';
import {useTranslation} from 'react-i18next';

import {NotificationTooltip, Icon, IconTypes, Button} from '@pexip/components';

import type {
    BlockedBrowserPermissionsInfoType,
    DeviceStatusInfo,
} from '../../types';
import {DeviceDeniedTooltip} from '../DeviceDeniedTooltip/DeviceDeniedTooltip.view';
import {TestId} from '../../../test/testIds';

import {InputControl} from './InputControl.view';

export type TooltipPosition =
    | 'topLeft'
    | 'topRight'
    | 'topCenter'
    | 'topStart'
    | 'topEnd'
    | 'bottomRight'
    | 'bottomCenter'
    | 'bottomLeft'
    | 'bottomStart'
    | 'bottomEnd'
    | undefined;

export const PreflightInputControl: React.FC<
    React.ComponentProps<typeof InputControl> & {
        className?: string;
        control?: React.ReactNode;
        inputStatusInfo?: DeviceStatusInfo;
        isDisabled?: boolean;
        isSuspended?: boolean;
        isTooltipVisible: boolean;
        learnHowToGrantAccessURL?: string;
        permissionInfoType?: BlockedBrowserPermissionsInfoType;
        requestMediaPermissions: () => void;
        setIsTooltipVisible: (value: React.SetStateAction<boolean>) => void;
        setShowHelpVideo: React.Dispatch<React.SetStateAction<boolean>>;
        testIdCloseButton?: string;
        toolTipTestId?: string;
        tooltipPosition?: TooltipPosition;
    }
> = ({
    control: customControl,
    inputStatusInfo,
    isSuspended,
    isTooltipVisible,
    learnHowToGrantAccessURL,
    permissionInfoType,
    requestMediaPermissions,
    setIsTooltipVisible,
    setShowHelpVideo,
    testIdCloseButton,
    toolTipTestId,
    tooltipPosition = 'topEnd',
    ...props
}) => {
    const {t} = useTranslation();
    const control =
        customControl &&
        !inputStatusInfo?.error &&
        !inputStatusInfo?.warning ? (
            customControl
        ) : (
            <InputControl
                {...props}
                variant={isSuspended ? 'translucent' : props.variant}
            />
        );

    return inputStatusInfo?.error &&
        inputStatusInfo?.controls === 'permissions' ? (
        <DeviceDeniedTooltip
            deniedDevice={inputStatusInfo.error}
            onClose={() => setIsTooltipVisible(!isTooltipVisible)}
            visible={isTooltipVisible}
            data-testid={TestId.TooltipDeniedDevice}
            setShowHelpVideo={setShowHelpVideo}
            learnHowToGrantAccessURL={learnHowToGrantAccessURL}
            permissionInfoType={permissionInfoType}
        >
            {control}
        </DeviceDeniedTooltip>
    ) : (
        <NotificationTooltip
            controlledIsVisible={isTooltipVisible}
            controlledOnChange={setIsTooltipVisible}
            ctaFn={
                inputStatusInfo?.retriable ? requestMediaPermissions : undefined
            }
            ctaText={
                inputStatusInfo?.retriable ? (
                    <Button
                        className="px-3"
                        isLoading={props.isLoading}
                        onClick={requestMediaPermissions}
                        size="small"
                        variant="primary"
                        enhancerStart={
                            <Icon
                                colorScheme="dark"
                                source={IconTypes.IconRefresh}
                            />
                        }
                    >
                        {t('common.try-again', 'Try again')}
                    </Button>
                ) : undefined
            }
            header={inputStatusInfo?.title}
            position={tooltipPosition}
            testIdCloseButton={testIdCloseButton}
            testIdInner={toolTipTestId}
            text={inputStatusInfo?.message ?? ''}
            closeButtonAriaLabel={t('common.close-tooltip', 'Close tooltip')}
        >
            {control}
        </NotificationTooltip>
    );
};
